import { lazy, Suspense, type ComponentProps } from 'react';

const Emoji = lazy(() => import('./EmojiPicker').then((m) => ({ default: m.EmojiPicker })));
const Poll = lazy(() => import('./dialogs/PollDialog').then((m) => ({ default: m.PollDialog })));
const Location = lazy(() => import('./dialogs/LocationDialog').then((m) => ({ default: m.LocationDialog })));
const PollBody = lazy(() => import('./PollContent').then((m) => ({ default: m.PollContent })));
const LocationBody = lazy(() => import('./LocationContent').then((m) => ({ default: m.LocationContent })));
const Bots = lazy(() => import('./dialogs/BotsDialog').then((m) => ({ default: m.BotsDialog })));
const Group = lazy(() => import('./dialogs/GroupDialog').then((m) => ({ default: m.GroupDialog })));
const Forward = lazy(() => import('./dialogs/ForwardDialog').then((m) => ({ default: m.ForwardDialog })));
const Devices = lazy(() => import('./dialogs/DevicesDialog').then((m) => ({ default: m.DevicesDialog })));
const Call = lazy(() => import('./CallHost').then((m) => ({ default: m.CallHost })));
const CallBtns = lazy(() => import('./CallHost').then((m) => ({ default: m.CallButtons })));
const CallLogBody = lazy(() => import('./CallHost').then((m) => ({ default: m.CallLog })));
const ReactionPickerBody = lazy(() => import('./ReactionPicker').then((m) => ({ default: m.ReactionPicker })));
const Audio = lazy(() => import('./AudioPlayer').then((m) => ({ default: m.AudioPlayer })));

/**
 * Các phần ít dùng được tách khỏi bundle chính (nạp khi mở lần đầu) để JS chính nằm trong ngân sách của docs/workspace-ui.md §6.
 * Bọc sẵn `Suspense` — nơi dùng không cần quan tâm.
 */
export const LazyEmojiPicker = (p: ComponentProps<typeof Emoji>) => (
  <Suspense fallback={null}>
    <Emoji {...p} />
  </Suspense>
);
export const LazyPollDialog = (p: ComponentProps<typeof Poll>) => (
  <Suspense fallback={null}>
    <Poll {...p} />
  </Suspense>
);
export const LazyLocationDialog = (p: ComponentProps<typeof Location>) => (
  <Suspense fallback={null}>
    <Location {...p} />
  </Suspense>
);
export const LazyPollContent = (p: ComponentProps<typeof PollBody>) => (
  <Suspense fallback={<div className="ws-poll ws-muted ws-small">Đang tải bình chọn…</div>}>
    <PollBody {...p} />
  </Suspense>
);
export const LazyLocationContent = (p: ComponentProps<typeof LocationBody>) => (
  <Suspense fallback={<div className="ws-location ws-muted ws-small">Đang tải vị trí…</div>}>
    <LocationBody {...p} />
  </Suspense>
);
export const LazyBotsDialog = (p: ComponentProps<typeof Bots>) => (
  <Suspense fallback={null}>
    <Bots {...p} />
  </Suspense>
);
export const LazyGroupDialog = (p: ComponentProps<typeof Group>) => (
  <Suspense fallback={null}>
    <Group {...p} />
  </Suspense>
);
export const LazyForwardDialog = (p: ComponentProps<typeof Forward>) => (
  <Suspense fallback={null}>
    <Forward {...p} />
  </Suspense>
);
export const LazyDevicesDialog = (p: ComponentProps<typeof Devices>) => (
  <Suspense fallback={null}>
    <Devices {...p} />
  </Suspense>
);
export const LazyAudioPlayer = (p: ComponentProps<typeof Audio>) => (
  <Suspense fallback={<div className="ws-audio ws-muted ws-small">Đang tải trình phát…</div>}>
    <Audio {...p} />
  </Suspense>
);

/** Cuộc gọi 1-1: nghe tín hiệu và vẽ lớp phủ cuộc gọi; luôn gắn trong Workspace nhưng nạp lười. */
export const LazyCallHost = () => (
  <Suspense fallback={null}>
    <Call />
  </Suspense>
);
export const LazyCallButtons = (p: ComponentProps<typeof CallBtns>) => (
  <Suspense fallback={null}>
    <CallBtns {...p} />
  </Suspense>
);
export const LazyCallLog = (p: ComponentProps<typeof CallLogBody>) => (
  <Suspense fallback={<span className="ws-muted ws-small">{p.text?.replace(/^📞 /, '')}</span>}>
    <CallLogBody {...p} />
  </Suspense>
);
export const LazyReactionPicker = (p: ComponentProps<typeof ReactionPickerBody>) => (
  <Suspense fallback={null}>
    <ReactionPickerBody {...p} />
  </Suspense>
);

const SettingsBody = lazy(() => import('./dialogs/SettingsDialog').then((m) => ({ default: m.SettingsDialog })));
const OrganizeBody = lazy(() => import('./dialogs/OrganizeDialog').then((m) => ({ default: m.OrganizeDialog })));
const AnnouncementsBody = lazy(() => import('./Announcements').then((m) => ({ default: m.Announcements })));
export const LazySettingsDialog = (p: ComponentProps<typeof SettingsBody>) => (
  <Suspense fallback={null}>
    <SettingsBody {...p} />
  </Suspense>
);
export const LazyOrganizeDialog = (p: ComponentProps<typeof OrganizeBody>) => (
  <Suspense fallback={null}>
    <OrganizeBody {...p} />
  </Suspense>
);
export const LazyAnnouncements = (p: ComponentProps<typeof AnnouncementsBody>) => (
  <Suspense fallback={<section className="ws-main ws-announce ws-muted">Đang tải…</section>}>
    <AnnouncementsBody {...p} />
  </Suspense>
);

const UserPanelBody = lazy(() => import('./UserPanel').then((m) => ({ default: m.UserPanel })));
const ConversationMenuBody = lazy(() => import('./ConversationMenu').then((m) => ({ default: m.ConversationMenu })));
export const LazyUserPanel = (p: ComponentProps<typeof UserPanelBody>) => (
  <Suspense fallback={null}>
    <UserPanelBody {...p} />
  </Suspense>
);
export const LazyConversationMenu = (p: ComponentProps<typeof ConversationMenuBody>) => (
  <Suspense fallback={null}>
    <ConversationMenuBody {...p} />
  </Suspense>
);
const ImageViewerBody = lazy(() => import('./ImageViewer').then((m) => ({ default: m.ImageViewer })));
export const LazyImageViewer = (p: ComponentProps<typeof ImageViewerBody>) => (
  <Suspense fallback={null}>
    <ImageViewerBody {...p} />
  </Suspense>
);

// Lưu trữ cá nhân / Công việc / Portal (dữ liệu mẫu cục bộ) — nạp khi mở mục tương ứng.
const StorageBody = lazy(() => import('../work/StorageScreen').then((m) => ({ default: m.StorageScreen })));
const TasksBody = lazy(() => import('../work/TasksScreen').then((m) => ({ default: m.TasksScreen })));
const PortalBody = lazy(() => import('../work/PortalScreen').then((m) => ({ default: m.PortalScreen })));
const workFallback = <section className="wk-screen ws-muted" style={{ padding: 24 }}>Đang tải…</section>;
export const LazyStorageScreen = () => (
  <Suspense fallback={workFallback}>
    <StorageBody />
  </Suspense>
);
export const LazyTasksScreen = () => (
  <Suspense fallback={workFallback}>
    <TasksBody />
  </Suspense>
);
export const LazyPortalScreen = () => (
  <Suspense fallback={workFallback}>
    <PortalBody />
  </Suspense>
);
